import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { captureRenderError } from "@/lib/observability";

interface Props {
  children: ReactNode;
  /** Optional renderer for a custom fallback UI. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
    captureRenderError(error, { componentStack: info.componentStack ?? undefined });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div role="alert" className="min-h-dvh flex items-center justify-center p-6 bg-background text-foreground">
        <div className="max-w-md w-full rounded-md border border-red-500/30 bg-red-500/5 p-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h1 className="text-base font-semibold tracking-tight">Something went wrong</h1>
          </div>
          <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">
            The page hit an unexpected error and could not finish rendering. Your saved progress is unaffected.
          </p>
          <details className="text-[11px] text-muted-foreground mb-4">
            <summary className="cursor-pointer mb-2">Technical details</summary>
            <pre className="whitespace-pre-wrap break-words bg-card/60 p-2 rounded text-[11px] border border-border">
              {error.message}
            </pre>
          </details>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex items-center gap-2 px-4 py-3 min-h-[44px] rounded-md bg-primary text-primary-foreground text-[13px] font-medium"
            >
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-4 py-3 min-h-[44px] rounded-md border border-border text-[13px] font-medium hover-elevate"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
